import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  submitEnterpriseOnboarding,
  EnterpriseOnboardingPayload,
  fetchCountries,
  fetchJurisdictionsByCountry,
  CountryOption,
  JurisdictionOption,
} from '../../../api/enterpriseApi';
import { JurisdictionPicker } from '../components/JurisdictionPicker';
import { cacheSet } from '../../../api/apiCache';
import { Eyebrow, PageHeading } from '../../../design-system';
import styles from './EnterpriseOnboardingPage.module.css';

// Defined outside the component to avoid remount-on-render focus loss
interface TagInputProps {
  label: string;
  value: string;
  tags: string[];
  onChange: (v: string) => void;
  onAdd: () => void;
  onRemove: (i: number) => void;
}
function TagInput({ label, value, tags, onChange, onAdd, onRemove }: TagInputProps) {
  return (
    <div className={styles.field}>
      <label>{label}</label>
      <div className={styles.tagInputRow}>
        <input
          value={value}
          onChange={e => onChange(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onAdd(); } }}
          placeholder="Type and press Enter"
        />
        <button type="button" className={styles.addBtn} onClick={onAdd}>Add</button>
      </div>
      <div className={styles.tags}>
        {tags.map((t, i) => (
          <span key={i} className={styles.tag}>
            {t}
            <button type="button" onClick={() => onRemove(i)}>×</button>
          </span>
        ))}
      </div>
    </div>
  );
}

// SRS #40: one required step (company + jurisdiction) and one optional step that can be skipped —
// the remaining details can be completed later from the enterprise profile.
const STEPS = ['Company & jurisdiction', 'Optional details'];

type FormState = {
  company_name: string;
  years_in_operation: string;
  // jurisdiction selection
  selectedCountryId: string;
  selectedCountryName: string;
  selectedJurisdictionId: string;
  selectedJurisdictionName: string;
  jurisdictionOther: boolean;
  customCountry: string;
  customJurisdiction: string;
  // registration
  mac_id: string;
  gst_number: string;
  founder_names: string[];
  // team
  num_members: string;
  existing_policies: string[];
  products_services: string;
  // multi-jurisdiction
  other_jurisdictions: string[];
  new_jurisdictions: string[];
  // tag inputs
  founderInput: string;
  policyInput: string;
};

const initial: FormState = {
  company_name: '',
  years_in_operation: '',
  selectedCountryId: '',
  selectedCountryName: '',
  selectedJurisdictionId: '',
  selectedJurisdictionName: '',
  jurisdictionOther: false,
  customCountry: '',
  customJurisdiction: '',
  mac_id: '',
  gst_number: '',
  founder_names: [],
  num_members: '',
  existing_policies: [],
  products_services: '',
  other_jurisdictions: [],
  new_jurisdictions: [],
  founderInput: '',
  policyInput: '',
};

export default function EnterpriseOnboardingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<FormState>(initial);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [jurisdictions, setJurisdictions] = useState<JurisdictionOption[]>([]);
  const [jurisLoading, setJurisLoading] = useState(false);

  useEffect(() => {
    fetchCountries().then(setCountries).catch(() => {});
  }, []);

  const set = (key: keyof FormState, value: any) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const handleCountryChange = async (countryId: string, countryName: string) => {
    set('selectedCountryId', countryId);
    set('selectedCountryName', countryName);
    set('selectedJurisdictionId', '');
    set('selectedJurisdictionName', '');
    set('jurisdictionOther', false);
    setJurisdictions([]);
    if (!countryId) return;
    setJurisLoading(true);
    try {
      const jurs = await fetchJurisdictionsByCountry(countryId);
      setJurisdictions(jurs);
    } catch { /* ignore */ }
    finally { setJurisLoading(false); }
  };

  const handleJurisdictionChange = (id: string, name: string) => {
    if (id === '__other__') {
      set('selectedJurisdictionId', '__other__');
      set('selectedJurisdictionName', '');
      set('jurisdictionOther', true);
    } else {
      set('selectedJurisdictionId', id);
      set('selectedJurisdictionName', name);
      set('jurisdictionOther', false);
    }
  };

  const resolvedJurisdiction = () => {
    if (form.selectedCountryId === '__other__' || form.jurisdictionOther) {
      return [form.customCountry, form.customJurisdiction].filter(Boolean).join(' – ');
    }
    return [form.selectedCountryName, form.selectedJurisdictionName].filter(Boolean).join(' – ');
  };

  const addTag = (listKey: keyof FormState, inputKey: keyof FormState) => {
    const val = (form[inputKey] as string)?.trim();
    if (!val) return;
    const existing = form[listKey] as string[];
    if (!existing.includes(val)) set(listKey, [...existing, val]);
    set(inputKey, '');
  };

  const removeTag = (listKey: keyof FormState, idx: number) => {
    const existing = form[listKey] as string[];
    set(listKey, existing.filter((_, i) => i !== idx));
  };

  const canNext = () => {
    if (step === 0) {
      if (!form.company_name.trim() || !form.years_in_operation.trim()) return false;
      if (form.selectedCountryId === '__other__') {
        return !!(form.customCountry.trim() && form.customJurisdiction.trim());
      }
      if (!form.selectedCountryId) return false;
      if (jurisdictions.length > 0 && !form.selectedJurisdictionId) return false;
      if (form.jurisdictionOther) return !!(form.customJurisdiction.trim());
      return true;
    }
    return true;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const payload: EnterpriseOnboardingPayload = {
        company_name: form.company_name,
        jurisdiction: resolvedJurisdiction(),
        years_in_operation: form.years_in_operation,
        mac_id: form.mac_id || undefined,
        gst_number: form.gst_number || undefined,
        founder_names: form.founder_names,
        num_members: form.num_members || undefined,
        existing_policies: form.existing_policies,
        products_services: form.products_services || undefined,
        other_jurisdictions: form.other_jurisdictions,
        new_jurisdictions: form.new_jurisdictions,
      };
      const saved = await submitEnterpriseOnboarding(payload);
      // Hand the saved profile (onboarding_completed=true) to EnterpriseRoute directly.
      // Invalidating alone left a window where the guard re-fetched or reused a
      // pre-submit 404 and bounced the user back to an empty form (seen 22 Sep).
      cacheSet('enterprise-profile', saved, 120_000);
      navigate('/enterprise/dashboard', { replace: true });
    } catch (err: any) {
      setError(err.message || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const isOtherCountry = form.selectedCountryId === '__other__';

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.header}>
          <Eyebrow tone="default" dot>ENTERPRISE SETUP</Eyebrow>
          <PageHeading
            accentWord="Enterprise"
            accentTone="orange"
            size="h2"
            align="left"
          >
            Welcome to Enterprise
          </PageHeading>
          <p>Tell us about your company to personalise your Policyora experience.</p>
        </div>

        <div className={styles.steps}>
          {STEPS.map((s, i) => (
            <div key={i} className={`${styles.stepItem} ${i === step ? styles.active : ''} ${i < step ? styles.done : ''}`}>
              <div className={styles.stepDot}>{i < step ? '✓' : i + 1}</div>
              <span className={styles.stepLabel}>{s}</span>
            </div>
          ))}
        </div>

        <div className={styles.body}>

          {/* ── Step 0: Company Info ── */}
          {step === 0 && (
            <>
              <div className={styles.field}>
                <label>Company Name <span className={styles.req}>*</span></label>
                <input value={form.company_name} onChange={e => set('company_name', e.target.value)} placeholder="Acme Corp" />
              </div>

              {/* Country selector */}
              <div className={styles.field}>
                <label>Country <span className={styles.req}>*</span></label>
                <select
                  value={form.selectedCountryId}
                  onChange={e => {
                    const opt = e.target.options[e.target.selectedIndex];
                    handleCountryChange(e.target.value, opt.dataset.name || '');
                  }}
                >
                  <option value="">Select country…</option>
                  {countries.map(c => (
                    <option key={c.id} value={c.id} data-name={c.name}>{c.name}</option>
                  ))}
                  <option value="__other__">Other (not listed)</option>
                </select>
              </div>

              {/* If other country → free-text inputs */}
              {isOtherCountry && (
                <>
                  <div className={styles.field}>
                    <label>Country Name <span className={styles.req}>*</span></label>
                    <input value={form.customCountry} onChange={e => set('customCountry', e.target.value)} placeholder="Enter country name" />
                  </div>
                  <div className={styles.field}>
                    <label>Jurisdiction / State / Region <span className={styles.req}>*</span></label>
                    <input value={form.customJurisdiction} onChange={e => set('customJurisdiction', e.target.value)} placeholder="Enter jurisdiction name" />
                  </div>
                </>
              )}

              {/* Jurisdiction dropdown — shown only when country is selected and has jurisdictions */}
              {!isOtherCountry && form.selectedCountryId && (
                <div className={styles.field}>
                  <label>Jurisdiction / State / Region <span className={styles.req}>*</span></label>
                  {jurisLoading ? (
                    <div className={styles.loadingText}>Loading jurisdictions…</div>
                  ) : (
                    <select
                      value={form.selectedJurisdictionId}
                      onChange={e => {
                        const opt = e.target.options[e.target.selectedIndex];
                        handleJurisdictionChange(e.target.value, opt.dataset.name || '');
                      }}
                    >
                      <option value="">Select jurisdiction…</option>
                      {jurisdictions.map(j => (
                        <option key={j.id} value={j.id} data-name={j.name}>{j.name}{j.type ? ` (${j.type})` : ''}</option>
                      ))}
                      <option value="__other__">Other (not listed)</option>
                    </select>
                  )}
                </div>
              )}

              {/* Custom jurisdiction text when "Other" is chosen in jurisdiction dropdown */}
              {!isOtherCountry && form.jurisdictionOther && (
                <div className={styles.field}>
                  <label>Jurisdiction Name <span className={styles.req}>*</span></label>
                  <input value={form.customJurisdiction} onChange={e => set('customJurisdiction', e.target.value)} placeholder="Enter jurisdiction name" />
                </div>
              )}

              {/* Preview */}
              {resolvedJurisdiction() && (
                <div className={styles.preview}>
                  <span className={styles.previewLabel}>Selected:</span> {resolvedJurisdiction()}
                </div>
              )}

              <div className={styles.field}>
                <label>Years in Operation <span className={styles.req}>*</span></label>
                <input value={form.years_in_operation} onChange={e => set('years_in_operation', e.target.value)} placeholder="e.g. 4" type="number" min="0" />
              </div>
            </>
          )}

          {/* ── Step 1 (optional): registration, team & policies, other jurisdictions ── */}
          {step === 1 && (
            <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--ds-text-muted)' }}>Everything below is optional — it sharpens governance and risk assessments, and can be completed later under Profile.</p>
          )}
          {step === 1 && (
            <>
              <div className={styles.field}>
                <label>MAC ID</label>
                <input value={form.mac_id} onChange={e => set('mac_id', e.target.value)} placeholder="Machine / entity MAC identifier" />
              </div>
              <div className={styles.field}>
                <label>GST Number</label>
                <input value={form.gst_number} onChange={e => set('gst_number', e.target.value)} placeholder="GST / Tax registration number" />
              </div>
              <TagInput
                label="Founder Names"
                value={form.founderInput}
                tags={form.founder_names}
                onChange={v => set('founderInput', v)}
                onAdd={() => addTag('founder_names', 'founderInput')}
                onRemove={i => removeTag('founder_names', i)}
              />
            </>
          )}

          {/* ── Step 2: Team & Policies ── */}
          {step === 1 && (
            <>
              <div className={styles.field}>
                <label>Number of Members / Employees</label>
                <input value={form.num_members} onChange={e => set('num_members', e.target.value)} placeholder="e.g. 250" type="number" min="1" />
              </div>
              <div className={styles.field}>
                <label>Products & Services</label>
                <textarea
                  value={form.products_services}
                  onChange={e => set('products_services', e.target.value)}
                  placeholder="Describe the products and services your company offers"
                  rows={3}
                />
              </div>
            </>
          )}

          {/* ── Step 3: Multi-jurisdiction ── */}
          {step === 1 && (
            <>
              <JurisdictionPicker
                label="Other Countries / Jurisdictions (already operating in)"
                tags={form.other_jurisdictions}
                onAdd={v => set('other_jurisdictions', [...form.other_jurisdictions, v])}
                onRemove={i => set('other_jurisdictions', form.other_jurisdictions.filter((_, idx) => idx !== i))}
              />
              <JurisdictionPicker
                label="New Jurisdictions to Establish In"
                tags={form.new_jurisdictions}
                onAdd={v => set('new_jurisdictions', [...form.new_jurisdictions, v])}
                onRemove={i => set('new_jurisdictions', form.new_jurisdictions.filter((_, idx) => idx !== i))}
              />
              <p className={styles.hint}>These will appear in your enterprise dashboard for multi-jurisdictional compliance tracking.</p>
            </>
          )}
        </div>

        {error && <p className={styles.error}>{error}</p>}

        <div className={styles.actions}>
          {step > 0 && (
            <button className={styles.backBtn} onClick={() => setStep(s => s - 1)}>Back</button>
          )}
          {step === 0 && (
            <button className={styles.backBtn} onClick={handleSubmit} disabled={!canNext() || submitting} title="Finish with just the company and jurisdiction; add the rest later in Profile">
              {submitting ? 'Submitting…' : 'Skip the rest & finish'}
            </button>
          )}
          {step < STEPS.length - 1 ? (
            <button className={styles.nextBtn} onClick={() => setStep(s => s + 1)} disabled={!canNext()}>
              Next
            </button>
          ) : (
            <button className={styles.submitBtn} onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Submitting…' : 'Complete Setup'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
